import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as services from "../../services/reportApi";
import * as service from "../../../employee/services/employeeApi";
import * as slices from "../../../employee/slices/employeeSlice";
import { setIsLoading } from "../../slices/reportSlice";
import { generateRandomString } from "utils/helper";

function useTerminateSocialSecurity() {
    const { i18n } = useTranslation;
    const dispatch = useDispatch();
    const [showReport, setShowReport] = useState(false);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployeeId = useSelector((state) => state.company.currentEmployee);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [search, setSearch] = useState({});
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [searchValues, setSearchValues] = useState({});
    const branchOptions = useSelector((state) => state.employee.branchOptions);
    const [isDownloading, setIsDownloading] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n?.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [departmentListOption, i18n]);

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions?.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n?.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions;
    }, [i18n, positionOptions]);

    const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
        const dispatch = useDispatch();
        useEffect(() => {
            const fetchData = async () => {
                dispatch(slices.setIsLoading(true));
                try {
                    const response = await serviceFunction({
                        requestId: generateRandomString(),
                        companyId,
                        groupType,
                    });
                    dispatch(dispatchFunction(response?.data));
                } catch (error) {
                    console.error('Failed to fetch data:', error);
                } finally {
                    dispatch(slices.setIsLoading(false));
                }
            };

            if (companyId) {
                fetchData();
            }
        }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
    };
    useFetchOptions(service.getBranch, currentCompany, "", slices.setBranchOptions);

    const modifiedOptions = (options) =>
        options?.map((opt) => ({
            ...opt,
            label: i18n?.language === "en" ? (opt?.nameEn ? opt?.nameEn : opt.name) : opt.name,
            value: opt.id,
        }));

    const modifiedBranchOptions = useMemo(() => modifiedOptions(branchOptions), [branchOptions, i18n?.language]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const previewSSOTerminateReportPdf = useCallback(async (params = {}, changeYear) => {
        try {
            dispatch(setIsLoading(true));

            const response = await services.ExportSSOResignationPdf({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...params,
                ...params.pagination,
                ...search,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            setPdfUrl(url);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [])

    const exportSSOTerminateReport = useCallback(
        async (params = {}, changeYear) => {
            try {
                dispatch(setIsLoading(true));
                setIsDownloading(true);
                const res = await services.ExportSSOResignationPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                });
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Terminate-SSO-Report.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloading(false);
            }
        },
        [currentCompany, dispatch, search, pagination]
    );

    useEffect(() => {
        if (currentCompany) {
            getDepartment();
            getPosition();
        }
    }, [currentCompany, getDepartment]);

    const handleSearch = (values) => {
        setShowReport(true);
        setSearchValues(values);
        setSearch({
            ...values,
            startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
            endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null
        });
        previewSSOTerminateReportPdf({
            ...values,
            startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
            endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null
        })
    };

    return {
        showModal,
        pdfUrl,
        showReport,
        isModalOpen,
        departmentOptions: modifiedDepartmentOption,
        positionOptions: modifiedPositionOptions,
        branchOptions: modifiedBranchOptions,
        searchValues,
        setIsModalOpen,
        handleSearch,
        exportSSOTerminateReport,
        isDownloading
    }
}
export default useTerminateSocialSecurity;